import React, { useState } from "react";
import {
	Alert,
	Box,
	Button,
	Container,
	PasswordInput,
	Paper,
	Stack,
	Text,
	Title
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconAlertTriangle } from "@tabler/icons-react";
import * as API from "../../helpers/api";
import config from "../../config";
import { setCookies } from "../../helpers/cookies";

export default function PasswordChangeGate({ user, onPasswordChanged, onLogout }) {
	const [currentPassword, setCurrentPassword] = useState("");
	const [newPassword, setNewPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [loading, setLoading] = useState(false);
	const isFirstTimeSetup = user?.passwordSetupRequired === true;

	const handleSubmit = async (event) => {
		event.preventDefault();

		if ((!isFirstTimeSetup && !currentPassword) || !newPassword) {
			notifications.show({
				color: "red",
				title: isFirstTimeSetup ? "Password setup required" : "Password change required",
				message: isFirstTimeSetup
					? "A new password is required."
					: "Current password and new password are required."
			});
			return;
		}

		if (newPassword !== confirmPassword) {
			notifications.show({
				color: "red",
				title: "Passwords do not match",
				message: "Please confirm the new password exactly."
			});
			return;
		}

		setLoading(true);
		try {
			const payload = isFirstTimeSetup
				? { newPassword }
				: { currentPassword, newPassword };
			const response = await API.post(config.api.changePassword, payload);

			if (response?.token) {
				setCookies(response.token);
			}

			if (typeof onPasswordChanged === "function") {
				onPasswordChanged(response);
			}

			setCurrentPassword("");
			setNewPassword("");
			setConfirmPassword("");
			notifications.show({
				color: "teal",
				title: isFirstTimeSetup ? "Password created" : "Password updated",
				message: isFirstTimeSetup
					? "Your password has been created successfully."
					: "Your password has been changed successfully."
			});
		} catch (error) {
			const message =
				error?.response?.data?.message ||
				error?.response?.data?.error ||
				error?.message ||
				"Unable to change password.";
			notifications.show({
				color: "red",
				title: "Password update failed",
				message
			});
		} finally {
			setLoading(false);
		}
	};

	return (
		<Box
			sx={(theme) => ({
				minHeight: "100vh",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				padding: theme.spacing.xl,
				background:
					theme.colorScheme === "dark"
						? `linear-gradient(145deg, ${theme.colors.dark[8]} 0%, ${theme.colors.dark[9]} 55%, ${theme.fn.rgba(theme.colors.orange[9], 0.18)} 100%)`
						: `linear-gradient(145deg, ${theme.colors.gray[0]} 0%, ${theme.white} 55%, ${theme.fn.rgba(theme.colors.orange[1], 0.75)} 100%)`
			})}
		>
			<Container size={460}>
				<Paper withBorder radius="lg" shadow="xl" p="xl">
					<Stack spacing="lg">
						<Stack spacing={6}>
							<Title order={2}>
								{isFirstTimeSetup ? "Password setup required" : "Password change required"}
							</Title>
							<Text color="dimmed" size="sm">
								{isFirstTimeSetup
									? (user?.username
										? `${user.username} must create a password before continuing.`
										: "You must create a password before continuing.")
									: (user?.username
										? `${user.username} must set a new password before continuing.`
										: "You must set a new password before continuing.")}
							</Text>
						</Stack>

						<Alert color="orange" icon={<IconAlertTriangle size={16} />}>
							{isFirstTimeSetup
								? "Platform access remains restricted until the first password is created. This is a real account control, not a lock screen."
								: "Platform access remains restricted until the password update is completed. This is a real account control, not a lock screen."}
						</Alert>

						<form onSubmit={handleSubmit}>
							<Stack spacing="md">
								{!isFirstTimeSetup ? (
									<PasswordInput
										label="Current password"
										placeholder="Enter your current password"
										value={currentPassword}
										onChange={(event) => setCurrentPassword(event.currentTarget.value)}
										required
									/>
								) : null}
								<PasswordInput
									label={isFirstTimeSetup ? "Create password" : "New password"}
									placeholder={isFirstTimeSetup ? "Create a password" : "Enter a new password"}
									value={newPassword}
									onChange={(event) => setNewPassword(event.currentTarget.value)}
									required
								/>
								<PasswordInput
									label="Confirm new password"
									placeholder="Repeat the new password"
									value={confirmPassword}
									onChange={(event) => setConfirmPassword(event.currentTarget.value)}
									required
								/>
								<Button type="submit" loading={loading} fullWidth>
									{isFirstTimeSetup ? "Set password" : "Update password"}
								</Button>
							</Stack>
						</form>

						<Button
							variant="subtle"
							color="gray"
							onClick={onLogout}
						>
							Sign out
						</Button>
					</Stack>
				</Paper>
			</Container>
		</Box>
	);
}
